export enum InitializationSteps {
  CREATE_BASKET = 'CREATE_BASKET',
  SET_WANTED_AT = 'SET_WANTED_AT',
  /**
   * Clearing a previously-set wanted_at back to ASAP (DELETE /wanted-time). Distinct from
   * SET_WANTED_AT so the failure copy and Sentry bucket say "clearing", not "setting".
   */
  REMOVE_WANTED_AT = 'REMOVE_WANTED_AT',
  SET_CONVEYANCE = 'SET_CONVEYANCE',
  VALIDATE = 'VALIDATE',
}

export enum InitializationErrorTypes {
  THROTTLING = 'throttling',
  HANDLED = 'handled',
  UNHANDLED = 'unhandled',
  LOCK = 'lock',
  PHONE = 'phone',
  TIME_OUT_OF_RANGE = 'time_out_of_range',
  /**
   * TEAM-15138: coo-api's `required_option_missing`. The only member classified from the top-level
   * `error` slug rather than from `INITIALIZATION_ERRORS` below — coo-api's message for it
   * interpolates an operator-authored option group name ("Product option group Rice requires a
   * minimum of 1 choices."), so no substring table over it could ever be complete, which is why
   * coo-api#368 gave the failure a slug and a structured `error_data`.
   *
   * Its recovery is neither a time picker nor a restart: the diner has to reopen the named cart
   * line and choose again. Restarting re-sends the same basket and fails identically, which is what
   * this type exists to stop the modal offering.
   */
  REQUIRED_OPTION = 'requiredOption',
}

/**
 * Top-level backend error `code` values for basket unavailable-item failures
 * (coo-api `Handler::unavailableItemsError`, contract from TEAM-12965). Every
 * flagged item unavailable because of a time-based menu transition (e.g. Lunch
 * -> Happy Hour) collapses to `ITEMS_OUT_OF_HOURS` (resolvable by changing the
 * order time); a suspended/missing item forces `ITEMS_UNAVAILABLE` (must be
 * removed). The inline unavailable-items notice branches its copy on this.
 */
export const UNAVAILABLE_ITEMS_ERROR_CODE = {
  ITEMS_OUT_OF_HOURS: 'items_out_of_hours',
  ITEMS_UNAVAILABLE: 'items_unavailable',
} as const;

export const INITIALIZATION_ERRORS: Record<string, InitializationErrorTypes> = {
  // THROTTLING
  'There are lots of orders coming in': InitializationErrorTypes.THROTTLING,
  'please choose another time': InitializationErrorTypes.THROTTLING,
  // P.F. Changs
  'experiencing a high order volume and cannot immediately start your order. Please select another time for pickup or delivery.':
    InitializationErrorTypes.THROTTLING,
  // Pancheros
  'is experiencing high order volume and cannot immediately start your order.':
    InitializationErrorTypes.THROTTLING,
  'high order volume and cannot prepare your order': InitializationErrorTypes.THROTTLING,
  // Scratch
  "We're sorry your order cannot be placed. The kitchen is really busy right now so please choose a time slot at least 30 minutes from now for us to prepare your food.":
    InitializationErrorTypes.THROTTLING,
  // Global
  "Sorry, it looks like your delivery destination is out of range, or there isn't an available driver for the delivery time you selected. Please schedule your delivery for another time or place your order for pickup.":
    InitializationErrorTypes.THROTTLING,
  'Delivery is not currently available to your address. Please try again later or order for Pickup!':
    InitializationErrorTypes.THROTTLING,
  'No delivery services are available to handle this order at this time.':
    InitializationErrorTypes.THROTTLING,
  'Please schedule your order for another time or select another order type.':
    InitializationErrorTypes.THROTTLING,
  'Please schedule your order for another time.': InitializationErrorTypes.THROTTLING,
  'A future date is required.': InitializationErrorTypes.THROTTLING,
  // The absence of a matching entry for the REMOVE_WANTED_AT copy ("There was an issue clearing
  // your selected ready time.") is intentional: THROTTLING routes to the time-picker recovery,
  // whose Apply button re-dispatches a permanently-'' wantedAt and so re-enters the same failing
  // clear in a loop. Revisit once TEAM-14373 / PR #792 lands.
  'There was an issue setting your desired ready time.': InitializationErrorTypes.THROTTLING,
  // HANDLED
  // KOALA
  'Please ensure that your delivery zip code is not longer than 6 digits.':
    InitializationErrorTypes.HANDLED,
  // OLO
  'More than one result was returned for this address. Please make this address as specific as possible.':
    InitializationErrorTypes.HANDLED,
  'not available for delivery orders': InitializationErrorTypes.HANDLED,
  'currently unavailable.': InitializationErrorTypes.HANDLED,
  'Choose between': InitializationErrorTypes.HANDLED, // Choose between 1 and 9 options for 'Dressings'
  "Your address is either outside of range or a driver isn't available at this time. Please select another method to receive your order.":
    InitializationErrorTypes.HANDLED,
  'A delivery address is required for this order.': InitializationErrorTypes.HANDLED,
  "We're sorry, some items in your cart are no longer available. Please review your cart and try again.":
    InitializationErrorTypes.HANDLED,
  "We're sorry - you're attempting to order delivery from too far away. You can still place your order for pick-up!":
    InitializationErrorTypes.HANDLED,
  'You have selected an item that is temporarily unavailable for online ordering.':
    InitializationErrorTypes.HANDLED,
  'remove any unavailable items': InitializationErrorTypes.HANDLED, // Apologies, there is a problem on this order. Please update your order to remove any unavailable items to try this order again. "Extra Sweet Chili Dipping Sauce" is not currently available on "5 Dumplings".
  'Only orders above': InitializationErrorTypes.HANDLED, // Only orders above {amount} can be delivered. Your order was not placed and you will not be billed.
  // LOCK
  "Oops, we're having trouble loading this page. Please try again soon":
    InitializationErrorTypes.LOCK,
  'Bad Gateway': InitializationErrorTypes.LOCK,
  'The special instructions must be a string., The location.id field is required when from order id is not present., The basket items field is required when from order id is not present.':
    InitializationErrorTypes.LOCK,
  'Unable to set conveyance': InitializationErrorTypes.LOCK,
  // PHONE
  'The phone number field is required.': InitializationErrorTypes.PHONE,
};

/** In Milliseconds. */
export const DELIVERY_STATUS_POLLING_INTERVAL = 60000;

export const DRIVER_NAME_DEFAULT_VALUE = 'Your Driver';
